import React, { useEffect, useState, useDeferredValue } from "react";
import api from "../api/api";
import { exportSectionsToCsv } from "../utils/exportToExcel";
import { formatDateDDMMYYYY } from "../utils/dateFormat";
import { getTotalsByColumn, isTotalsTable } from "../utils/tableTotals";

const REPORT_TYPE = "DTA Receipts";

const DTAReceipts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [rowHeight, setRowHeight] = useState("compact");
  const [fullscreenTable, setFullscreenTable] = useState(null);
  const [fontScale, setFontScale] = useState(100);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const userName = sessionStorage.getItem("userName");
      const response = await api.GetBoEDatas(userName, REPORT_TYPE);
      setData(response?.BoEData || response);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterTableData = (tableData) => {
    if (!tableData || !deferredSearchQuery) return tableData;
    const normalizedSearch = deferredSearchQuery.toLowerCase();

    return tableData.filter((row) =>
      Object.values(row).some(
        (value) =>
          value !== null &&
          value !== undefined &&
          String(value).toLowerCase().includes(normalizedSearch)
      )
    );
  };

  const isNumericValue = (value) => {
    if (typeof value === "number") return Number.isFinite(value);
    if (typeof value !== "string") return false;
    return /^-?\d+(\.\d+)?$/.test(value.trim().replace(/,/g, ""));
  };

  const isColumnConfigTable = (table) => {
    return (
      Array.isArray(table) &&
      table.length > 0 &&
      ["DbColDisplay", "DisplayColName", "Visible", "Priority"].every(
        (key) => key in table[0]
      )
    );
  };

  const normalizeColumnName = (value) =>
    String(value || "")
      .replace(/[_\s]/g, "")
      .toLowerCase();

  const resolveConfigColumn = (cfg, defaultColumns) => {
    if (cfg.DbColDisplay && defaultColumns.includes(cfg.DbColDisplay)) {
      return cfg.DbColDisplay;
    }

    if (cfg.DisplayColName && defaultColumns.includes(cfg.DisplayColName)) {
      return cfg.DisplayColName;
    }

    const normalizedDbColumn = normalizeColumnName(cfg.DbColDisplay);
    const normalizedDisplayColumn = normalizeColumnName(cfg.DisplayColName);

    return defaultColumns.find((col) => {
      const normalizedColumn = normalizeColumnName(col);
      return (
        normalizedColumn === normalizedDbColumn ||
        normalizedColumn === normalizedDisplayColumn
      );
    });
  };

  const getOrderedColumns = (tableData, columnConfig) => {
    const defaultColumns = Object.keys(tableData[0] || {});
    if (!columnConfig) return defaultColumns;

    const configuredColumns = new Set(
      columnConfig
        .map((cfg) => resolveConfigColumn(cfg, defaultColumns))
        .filter(Boolean)
    );

    const configColumns = columnConfig
      .filter((cfg) => Number(cfg.Visible) !== 0 && cfg.DbColDisplay)
      .sort((a, b) => Number(a.Priority || 9999) - Number(b.Priority || 9999))
      .map((cfg) => resolveConfigColumn(cfg, defaultColumns))
      .filter(Boolean);

    const remainingColumns = defaultColumns.filter((col) => !configuredColumns.has(col));
    return [...configColumns, ...remainingColumns];
  };

  const getDisplayNameMap = (columnConfig, tableData = []) => {
    if (!columnConfig) return {};
    const defaultColumns = Object.keys(tableData[0] || {});

    return columnConfig.reduce((acc, cfg) => {
      const columnName = resolveConfigColumn(cfg, defaultColumns);
      if (columnName) acc[columnName] = cfg.DisplayColName || columnName;
      return acc;
    }, {});
  };

  const getColumnLayout = (col, isNumeric = false) => {
    const normalized = normalizeColumnName(col);

    if (normalized === "slno") {
      return {
        headerClassName: "text-center",
        cellClassName: "text-center whitespace-nowrap",
        style: { width: "56px", minWidth: "56px", maxWidth: "56px" },
      };
    }

    if (normalized === "gstinvoicenodate") {
      return {
        headerClassName: "text-left",
        cellClassName: "text-left whitespace-normal break-words",
        style: { width: "170px", minWidth: "150px", maxWidth: "190px" },
      };
    }

    if (normalized === "descriptionofgoods") {
      return {
        headerClassName: "text-left",
        cellClassName: "text-left whitespace-normal break-words",
        style: { width: "320px", minWidth: "220px", maxWidth: "360px" },
      };
    }

    if (normalized === "uqc") {
      return {
        headerClassName: "text-left",
        cellClassName: "text-left whitespace-nowrap",
        style: { width: "72px", minWidth: "64px", maxWidth: "80px" },
      };
    }

    return {
      headerClassName: isNumeric ? "text-right" : "text-left",
      cellClassName: `${isNumeric ? "text-right" : "text-left"} whitespace-nowrap`,
      style: isNumeric
        ? { width: "116px", minWidth: "104px", maxWidth: "132px" }
        : {},
    };
  };

  const formatDate = (dateString) => formatDateDDMMYYYY(dateString);

  const shouldFormatAsDate = (col) => {
    const normalized = normalizeColumnName(col);
    return normalized.includes("date") && normalized !== "gstinvoicenodate";
  };

  const renderTable = (
    tableData,
    title,
    tableKey = null,
    columnConfig = null,
    isFullscreen = false,
    totalsTable = null
  ) => {
    if (!tableData || tableData.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm p-3">
          {title && <h2 className="text-lg font-semibold mb-2">{title}</h2>}
          <p className="text-gray-400">No data available</p>
        </div>
      );
    }

    const columns = getOrderedColumns(tableData, columnConfig);
    const displayNameMap = getDisplayNameMap(columnConfig, tableData);
    const numericColumns = new Set(
      columns.filter((col) => tableData.some((row) => isNumericValue(row[col])))
    );
    const totalsByColumn = getTotalsByColumn(totalsTable, columns);
    const cellPaddingStyle =
      rowHeight === "compact"
        ? { padding: "3px 6px" }
        : { padding: "8px 16px" };

    return (
      <div className="bg-white rounded-lg shadow-sm">
        <div className={`overflow-auto ${isFullscreen ? "max-h-[calc(100vh-56px)]" : "max-h-[76vh]"}`}>
          <table
            className="min-w-full text-sm border-collapse"
            style={{ borderSpacing: "0", fontSize: `${fontScale}%` }}
          >
            <thead className="sticky top-0 bg-gray-100 z-10">
              <tr>
                {columns.map((col) => {
                  const isNumeric = numericColumns.has(col);
                  const displayLabel = displayNameMap[col] || col.replace(/_/g, " ");
                  const totalValue = totalsByColumn[col];
                  const columnLayout = getColumnLayout(col, isNumeric);

                  return (
                    <th
                      key={col}
                      className={`font-bold text-gray-700 whitespace-normal break-words align-middle border-b ${
                        columnLayout.headerClassName
                      }`}
                      style={{
                        ...columnLayout.style,
                        paddingLeft: "8px",
                        paddingRight: "8px",
                        paddingTop: "10px",
                        paddingBottom: "10px",
                        minHeight: "52px",
                        lineHeight: "1.2",
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="leading-tight">{displayLabel}</span>
                        <span
                          className={`text-sm font-medium leading-tight min-h-[18px] ${
                            totalValue !== undefined ? "text-emerald-700" : "text-transparent"
                          }`}
                        >
                          {totalValue !== undefined ? totalValue : "\u00A0"}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, i) => (
                <tr
                  key={`${tableKey || "dta"}-${i}`}
                  onClick={() => setSelectedRowIndex(selectedRowIndex === i ? null : i)}
                  className={`border-b last:border-none cursor-pointer transition-colors ${
                    selectedRowIndex === i
                      ? "bg-blue-200 hover:bg-blue-300"
                      : i % 2 === 0
                      ? "bg-gray-50 hover:bg-gray-100"
                      : "bg-white hover:bg-gray-50"
                  }`}
                >
                  {columns.map((col) => {
                    let value = row[col];
                    const wasNumeric = isNumericValue(row[col]);
                    const isNumeric = numericColumns.has(col);
                    const columnLayout = getColumnLayout(col, isNumeric || wasNumeric);

                    if (shouldFormatAsDate(col)) {
                      value = formatDate(value);
                    } else if (typeof value === "number") {
                      value = value.toLocaleString();
                    } else if (value === null || value === undefined) {
                      value = "-";
                    }

                    return (
                      <td
                        key={col}
                        className={`${
                          selectedRowIndex === i ? "font-semibold text-gray-900" : "text-gray-700"
                        } ${columnLayout.cellClassName}`}
                        style={{ ...columnLayout.style, ...cellPaddingStyle }}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const handleExportToExcel = () => {
    if (!data) return;

    const sections = [];
    const addSection = (tableRows, title, columnConfig = null) => {
      const filteredRows = filterTableData(tableRows);
      if (!filteredRows || filteredRows.length === 0) return;

      const columns = columnConfig
        ? getOrderedColumns(filteredRows, columnConfig)
        : Object.keys(filteredRows[0] || {});
      if (!columns.length) return;

      const displayNameMap = columnConfig ? getDisplayNameMap(columnConfig, filteredRows) : {};
      const headers = columns.map((col) => displayNameMap[col] || col.replace(/_/g, " "));
      const records = filteredRows.map((row) =>
        columns.map((col) => {
          const value = row[col];
          if (shouldFormatAsDate(col)) return formatDate(value);
          if (value === null || value === undefined) return "-";
          return value;
        })
      );

      sections.push({ title, headers, records });
    };

    const mainConfig = isColumnConfigTable(data.Table1) ? data.Table1 : null;
    addSection(data.Table, REPORT_TYPE, mainConfig);
    if (!isColumnConfigTable(data.Table1)) addSection(data.Table1, "Additional Details");
    addSection(data.Table2, "Totals");

    exportSectionsToCsv({ sections, fileNamePrefix: "DTA_Receipts" });
  };

  const mainConfig = isColumnConfigTable(data?.Table1) ? data.Table1 : null;
  const mainColumns = data?.Table?.length ? getOrderedColumns(data.Table, mainConfig) : [];
  const mainTotals = isTotalsTable(data?.Table2, mainColumns) ? data.Table2 : null;

  return (
    <div className="p-1 space-y-1 bg-gray-50 min-h-screen">
      <div className="bg-white px-3 py-2 rounded-lg shadow-sm flex items-center justify-between">
        <div className="flex-1 text-center">
          <h1 className="text-lg font-bold">{REPORT_TYPE}</h1>
        </div>
        <span className="text-xs text-gray-400 whitespace-nowrap">
          {new Date().toLocaleString()}
        </span>
      </div>

      <div className="bg-white p-2 rounded-lg shadow-sm space-y-2">
        <input
          type="text"
          placeholder="Search records..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Row Height:</span>
            <button
              onClick={() => setRowHeight(rowHeight === "compact" ? "normal" : "compact")}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                rowHeight === "compact"
                  ? "bg-blue-500 hover:bg-blue-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300"
              }`}
              title="Toggle Row Height"
            >
              {rowHeight === "compact" ? "Compact" : "Normal"}
            </button>
          </div>
          <div className="flex items-center gap-1 p-1 bg-gray-100 border border-gray-200 rounded-md">
            <button
              onClick={() => setFontScale((prev) => Math.max(80, prev - 10))}
              className="px-2 py-1 rounded text-xs font-semibold bg-gray-200 hover:bg-gray-300 text-gray-700"
              title="Decrease Font Size"
            >
              A-
            </button>
            <button
              onClick={() => setFontScale((prev) => Math.min(140, prev + 10))}
              className="px-2 py-1 rounded text-xs font-semibold bg-gray-200 hover:bg-gray-300 text-gray-700"
              title="Increase Font Size"
            >
              A+
            </button>
            <button
              onClick={handleExportToExcel}
              className="px-2 py-1 rounded text-xs font-semibold bg-emerald-100 hover:bg-emerald-200 text-emerald-800"
              title="Export to Excel"
            >
              Export
            </button>
            <button
              onClick={() => setFullscreenTable(fullscreenTable === "table1" ? null : "table1")}
              className="p-2 rounded transition-colors bg-gray-200 hover:bg-gray-300 text-gray-700"
              title="Toggle Fullscreen"
            >
              {fullscreenTable === "table1" ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3H5a2 2 0 00-2 2v3m13-5h3a2 2 0 012 2v3M8 21H5a2 2 0 01-2-2v-3m16 5h-3a2 2 0 01-2-2v-3" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-5v4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 0h-4m4 0l-5-5" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-6">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-500"></div>
        </div>
      )}

      {!loading && data && (
        <div className="space-y-1">
          {data.Table &&
            renderTable(filterTableData(data.Table), null, "table1", mainConfig, false, mainTotals)}
          {data.Table1 &&
            !isColumnConfigTable(data.Table1) &&
            renderTable(filterTableData(data.Table1), "Additional Details", "table2")}
          {data.Table2 && !mainTotals && renderTable(filterTableData(data.Table2), "Totals", "table3")}
        </div>
      )}

      {!loading && !data && (
        <div className="bg-white rounded-lg shadow-sm p-4 text-center">
          <p className="text-gray-500">No data available</p>
        </div>
      )}

      {fullscreenTable && (
        <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col">
          <div className="bg-white border-b sticky top-0 z-50 px-3 py-2">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold">Expanded View</h2>
              <button
                onClick={() => setFullscreenTable(null)}
                className="px-3 py-2 rounded text-sm font-medium transition-colors bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800"
                title="Close Fullscreen"
              >
                Close
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {renderTable(
              filterTableData(data?.Table),
              REPORT_TYPE,
              "table1",
              mainConfig,
              true,
              mainTotals
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DTAReceipts;
